import { useId } from "react";
import { cn } from "@/lib/cn";
import type { FunnelStep, Point } from "@/mock";
import { fmt } from "@/lib/format";

/* Build a smooth-ish SVG path (quadratic through midpoints) from points. */
function buildPath(points: Point[], w: number, h: number, pad: number) {
  const n = points.length;
  if (n === 0) return { line: "", area: "", last: { x: 0, y: 0 } };
  const vals = points.map((p) => p.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const xy = points.map((p, i) => ({
    x: pad + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW),
    y: pad + innerH - ((p.v - min) / span) * innerH,
  }));
  let line = `M ${xy[0]!.x} ${xy[0]!.y}`;
  for (let i = 1; i < xy.length; i++) {
    const prev = xy[i - 1]!;
    const cur = xy[i]!;
    const mx = (prev.x + cur.x) / 2;
    line += ` Q ${prev.x} ${prev.y} ${mx} ${(prev.y + cur.y) / 2} T ${cur.x} ${cur.y}`;
  }
  const area = `${line} L ${xy[n - 1]!.x} ${h - pad} L ${xy[0]!.x} ${h - pad} Z`;
  return { line, area, last: xy[n - 1]! };
}

/** Tiny inline trend line. color: "ink" | "accent" | "gold". */
export function Sparkline({
  data,
  color = "ink",
  height = 38,
  className,
  fill = true,
}: {
  data: Point[];
  color?: "ink" | "accent" | "gold";
  height?: number;
  className?: string;
  fill?: boolean;
}) {
  const id = useId();
  const W = 220;
  const { line, area, last } = buildPath(data, W, height, 4);
  const stroke =
    color === "accent" ? "rgb(var(--c-accent))" : color === "gold" ? "rgb(var(--c-gold))" : "rgb(var(--c-ink-600))";
  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      className={cn("w-full", className)}
      style={{ height }}
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={`sp-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#sp-${id})`} />
        </>
      )}
      <path d={line} fill="none" stroke={stroke} strokeWidth={1.75} vectorEffect="non-scaling-stroke" />
      <circle cx={last.x} cy={last.y} r={2.6} fill={stroke} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Main time-series chart. Primary = ink line + coral last-point; optional gold 2nd series. */
export function LineChart({
  a,
  b,
  height = 220,
  grid = true,
  labels = true,
  className,
}: {
  a: Point[];
  b?: Point[];
  height?: number;
  grid?: boolean;
  labels?: boolean;
  className?: string;
}) {
  const id = useId();
  const W = 640;
  const pad = 14;
  const pa = buildPath(a, W, height, pad);
  const pb = b ? buildPath(b, W, height, pad) : null;
  const rows = [0.25, 0.5, 0.75];
  return (
    <div className={className}>
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
        {grid &&
          rows.map((r) => (
            <line
              key={r}
              x1={pad}
              x2={W - pad}
              y1={pad + (height - pad * 2) * r}
              y2={pad + (height - pad * 2) * r}
              stroke="var(--grid-line)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        <defs>
          <linearGradient id={`ln-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(var(--c-ink-600))" stopOpacity="0.12" />
            <stop offset="100%" stopColor="rgb(var(--c-ink-600))" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={pa.area} fill={`url(#ln-${id})`} />
        {pb && (
          <path
            d={pb.line}
            fill="none"
            stroke="rgb(var(--c-gold))"
            strokeWidth={1.75}
            strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <path d={pa.line} fill="none" stroke="rgb(var(--c-ink-600))" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        <line
          x1={pa.last.x}
          x2={pa.last.x}
          y1={pad}
          y2={height - pad}
          stroke="rgb(var(--c-accent))"
          strokeWidth={1}
          strokeDasharray="2 3"
          vectorEffect="non-scaling-stroke"
          opacity={0.5}
        />
        <circle cx={pa.last.x} cy={pa.last.y} r={3.5} fill="rgb(var(--c-accent))" vectorEffect="non-scaling-stroke" />
      </svg>
      {labels && (
        <div className="mt-2 flex justify-between px-1 font-mono text-[10px] text-subtle">
          {a.map((p, i) => (
            <span key={i} className={i % 2 && a.length > 8 ? "hidden sm:inline" : ""}>
              {p.t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Horizontal rank bars used for top pages / sources / channels / geo. */
export function Bars({
  data,
  className,
  accentTop,
}: {
  data: { label: string; value: number; sub?: string }[];
  className?: string;
  accentTop?: boolean;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      {data.map((d, i) => (
        <div key={d.label} className="group">
          <div className="mb-1 flex items-baseline justify-between gap-2 text-[13px]">
            <span className="truncate text-ink">{d.label}</span>
            <span className="shrink-0 font-mono text-[12px] text-muted">
              {fmt(d.value)}
              {d.sub ? <span className="ml-1.5 text-subtle">{d.sub}</span> : null}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-paper-2 dark:bg-white/5">
            <div
              className={cn(
                "h-full rounded-full transition-[width] duration-500 ease-out-soft",
                accentTop && i === 0 ? "bg-accent" : "bg-ink-strong/70 dark:bg-white/40",
              )}
              style={{ width: `${(d.value / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Funnel — ink bars, coral focal (last) step, drop-off %. */
export function Funnel({ steps, className }: { steps: FunnelStep[]; className?: string }) {
  const top = steps[0]?.value || 1;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {steps.map((s, i) => {
        const pct = (s.value / top) * 100;
        const prev = i === 0 ? s.value : steps[i - 1]!.value;
        const drop = i === 0 ? 0 : Math.round((1 - s.value / prev) * 100);
        const last = i === steps.length - 1;
        return (
          <div key={s.label}>
            <div className="mb-1 flex items-baseline justify-between text-[13px]">
              <span className="text-ink">{s.label}</span>
              <span className="font-mono text-[12px] text-muted">
                {fmt(s.value)} · {pct.toFixed(0)}%
              </span>
            </div>
            <div className="h-7 w-full overflow-hidden rounded-md bg-paper-2 dark:bg-white/5">
              <div
                className={cn(
                  "flex h-full items-center justify-end rounded-md px-2 transition-[width] duration-500 ease-out-soft",
                  last ? "bg-accent" : "bg-ink-strong/80 dark:bg-white/30",
                )}
                style={{ width: `${pct}%` }}
              >
                {i > 0 && (
                  <span className={cn("font-mono text-[10px]", last ? "text-white/90" : "text-canvas/80")}>
                    −{drop}%
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Heat blobs overlaid on a page thumbnail. points: {x,y,weight} in 0..1. */
export function HeatmapOverlay({
  points,
  className,
}: {
  points: { x: number; y: number; w: number }[];
  className?: string;
}) {
  return (
    <div className={cn("pointer-events-none absolute inset-0", className)} aria-hidden>
      {points.map((p, i) => (
        <div
          key={i}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full mix-blend-multiply dark:mix-blend-screen"
          style={{
            left: `${p.x * 100}%`,
            top: `${p.y * 100}%`,
            width: `${40 + p.w * 110}px`,
            height: `${40 + p.w * 110}px`,
            background: `radial-gradient(circle, rgba(184,68,34,${0.5 * p.w}) 0%, rgba(200,146,61,${0.32 * p.w}) 38%, transparent 70%)`,
          }}
        />
      ))}
    </div>
  );
}
